class InterviewSlider {
  constructor(el) {
    this.root = document.querySelector('.' + el);

    if (!this.root) {
      return;
    }

    this.mainImage = this.root.querySelector('[data-interview-main-image]');
    this.mainSource = this.root.querySelector('[data-interview-main-source]');
    this.swipeTargets = Array.from(this.root.querySelectorAll('[data-interview-swipe]'));
    this.number = this.root.querySelector('[data-interview-number]');
    this.role = this.root.querySelector('[data-interview-role]');
    this.quote = this.root.querySelector('[data-interview-quote]');
    this.profile = this.root.querySelector('[data-interview-profile]');
    this.moreLink = this.root.querySelector('[data-interview-more]');
    this.spotlight = this.root.querySelector('.p-top__interview-spotlight');
    this.viewport = this.root.querySelector('.p-top__interview-thumbnails');
    this.track = this.root.querySelector('[data-interview-thumbnail-track]');
    this.thumbnails = Array.from(this.root.querySelectorAll('[data-interview-thumbnail]'));
    this.prevButton = this.root.querySelector('[data-interview-gallery-prev]');
    this.nextButton = this.root.querySelector('[data-interview-gallery-next]');
    this.thumbnailPage = 0;
    this.currentIndex = 0;
    this.isAnimating = false;
    this.timeline = null;
    this.spotlightParallaxFrame = null;
    this.setSpotlightY = null;
    this.swipe = {
      active: false,
      startX: 0,
      startY: 0,
      locked: null,
    };

    this.event();
    this.setActiveThumbnail(0);
    this.updateContent(0);
    this.updateThumbnailPosition();
  }

  event() {
    this.thumbnails.forEach((thumbnail, index) => {
      thumbnail.addEventListener('click', () => {
        this.show(index);
      });
    });

    if (this.prevButton) {
      this.prevButton.addEventListener('click', () => {
        this.showPrevSlide();
      });
    }

    if (this.nextButton) {
      this.nextButton.addEventListener('click', () => {
        this.showNextSlide();
      });
    }

    window.addEventListener('resize', () => {
      this.updateThumbnailPosition();
    });

    this.setupSwipe();
    this.setupSpotlightParallax();
  }

  isSpViewport() {
    return window.matchMedia('(max-width: 767px)').matches;
  }

  setupSwipe() {
    if (!this.swipeTargets.length) {
      return;
    }

    this.swipeTargets.forEach((target) => {
      target.addEventListener('touchstart', (event) => {
        this.onSwipeStart(event);
      }, { passive: true });

      target.addEventListener('touchmove', (event) => {
        this.onSwipeMove(event);
      }, { passive: false });

      target.addEventListener('touchend', (event) => {
        this.onSwipeEnd(event);
      });

      target.addEventListener('touchcancel', () => {
        this.resetSwipe();
      });
    });
  }

  isSwipeIgnoredTarget(event) {
    return Boolean(event.target.closest('a, button'));
  }

  onSwipeStart(event) {
    if (!this.isSpViewport() || event.touches.length !== 1) {
      return;
    }

    if (this.isSwipeIgnoredTarget(event)) {
      return;
    }

    const touch = event.touches[0];
    this.swipe.active = true;
    this.swipe.startX = touch.clientX;
    this.swipe.startY = touch.clientY;
    this.swipe.locked = null;
  }

  onSwipeMove(event) {
    if (!this.swipe.active || event.touches.length !== 1) {
      return;
    }

    const touch = event.touches[0];
    const dx = touch.clientX - this.swipe.startX;
    const dy = touch.clientY - this.swipe.startY;
    const lockThreshold = 8;

    if (this.swipe.locked === null && (Math.abs(dx) > lockThreshold || Math.abs(dy) > lockThreshold)) {
      this.swipe.locked = Math.abs(dx) > Math.abs(dy) ? 'x' : 'y';
    }

    if (this.swipe.locked === 'x') {
      event.preventDefault();
    }
  }

  onSwipeEnd(event) {
    if (!this.swipe.active) {
      return;
    }

    const touch = event.changedTouches[0];

    if (!touch) {
      this.resetSwipe();
      return;
    }

    const dx = touch.clientX - this.swipe.startX;
    const wasHorizontal = this.swipe.locked === 'x';
    const swipeThreshold = 40;

    this.resetSwipe();

    if (!wasHorizontal || Math.abs(dx) < swipeThreshold) {
      return;
    }

    if (dx < 0) {
      this.showNextSlide();
      return;
    }

    this.showPrevSlide();
  }

  resetSwipe() {
    this.swipe.active = false;
    this.swipe.startX = 0;
    this.swipe.startY = 0;
    this.swipe.locked = null;
  }

  setupSpotlightParallax() {
    const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    if (!this.spotlight || reduceMotion) {
      return;
    }

    if (typeof gsap !== 'undefined') {
      this.setSpotlightY = gsap.quickTo(this.spotlight, 'y', {
        duration: 1.2,
        ease: 'power3.out',
      });
    }

    const requestUpdate = () => {
      if (this.spotlightParallaxFrame !== null) {
        return;
      }

      this.spotlightParallaxFrame = window.requestAnimationFrame(() => {
        this.spotlightParallaxFrame = null;
        this.updateSpotlightParallax();
      });
    };

    window.addEventListener('scroll', requestUpdate, { passive: true });
    window.addEventListener('resize', requestUpdate);
    requestUpdate();
  }

  updateSpotlightParallax() {
    const rootRect = this.root.getBoundingClientRect();
    const viewportCenter = window.innerHeight / 2;
    const rootCenter = rootRect.top + (rootRect.height / 2);
    const travelRange = (window.innerHeight + rootRect.height) / 2;
    const progress = (viewportCenter - rootCenter) / travelRange;
    const clampedProgress = Math.max(-1, Math.min(1, progress));
    const rootFontSize = parseFloat(window.getComputedStyle(document.documentElement).fontSize);
    const offset = clampedProgress * rootFontSize * 4;

    if (this.setSpotlightY) {
      this.setSpotlightY(offset);
      return;
    }

    this.spotlight.style.transform = `translate3d(0, ${offset}px, 0)`;
  }

  show(index) {
    const thumbnail = this.thumbnails[index];

    if (!thumbnail || index === this.currentIndex || this.isAnimating) {
      return;
    }

    this.setActiveThumbnail(index);

    if (typeof gsap === 'undefined' || window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      this.updateContent(index);
      return;
    }

    this.isAnimating = true;

    this.preloadImage(this.slideImageSrc(thumbnail)).then(() => {
      this.animateSlideChange(index);
    });
  }

  animateSlideChange(index) {
    const copy = this.root.querySelector('.p-top__interview-copy');
    const maskTargets = [this.mainImage, copy].filter(Boolean);

    this.timeline = gsap.timeline({
      defaults: {
        duration: 0.5,
        ease: 'power2.inOut',
      },
      onComplete: () => {
        this.isAnimating = false;
        this.timeline = null;
      },
    });

    this.timeline
      .addLabel('hide')
      .to(this.mainImage, {
        clipPath: 'inset(0 0 0 100%)',
      }, 'hide')
      .to(copy, {
        clipPath: 'inset(0 0 0 100%)',
      }, 'hide')
      .call(() => {
        this.updateContent(index);
        gsap.set(maskTargets, {
          clipPath: 'inset(0 100% 0 0)',
        });
      }, null, 'hide+=0.5')
      .addLabel('show', 'hide+=0.5')
      .to(this.mainImage, {
        clipPath: 'inset(0 0% 0 0)',
        ease: 'power3.out',
      }, 'show')
      .to(copy, {
        clipPath: 'inset(0 0% 0 0)',
        ease: 'power3.out',
      }, 'show');
  }

  slideImageSrc(thumbnail) {
    if (this.isSpViewport() && thumbnail.dataset.interviewImageSp) {
      return thumbnail.dataset.interviewImageSp;
    }
    return thumbnail.dataset.interviewImage;
  }

  preloadImage(src) {
    return new Promise((resolve) => {
      const image = new Image();

      image.onload = resolve;
      image.onerror = resolve;
      image.src = src;
    });
  }

  setActiveThumbnail(index) {
    this.thumbnails.forEach((item, itemIndex) => {
      const isActive = itemIndex === index;
      item.classList.toggle('is-active', isActive);
      item.setAttribute('aria-pressed', String(isActive));
    });

    this.currentIndex = index;
    this.syncThumbnailPage(index);
  }

  updateContent(index) {
    const thumbnail = this.thumbnails[index];

    if (!thumbnail) {
      return;
    }

    this.mainImage.src = thumbnail.dataset.interviewImage;
    if (this.mainSource) {
      this.mainSource.srcset = thumbnail.dataset.interviewImageSp || thumbnail.dataset.interviewImage;
    }
    this.mainImage.alt = thumbnail.dataset.interviewAlt;
    this.number.textContent = thumbnail.dataset.interviewNumber;
    this.role.textContent = thumbnail.dataset.interviewRole;
    this.quote.textContent = thumbnail.dataset.interviewQuote;
    this.profile.textContent = thumbnail.dataset.interviewProfile;

    if (this.moreLink && thumbnail.dataset.interviewUrl) {
      this.moreLink.setAttribute('href', thumbnail.dataset.interviewUrl);
    }
  }

  showPrevSlide() {
    this.show(this.currentIndex - 1);
  }

  showNextSlide() {
    this.show(this.currentIndex + 1);
  }

  syncThumbnailPage(index) {
    const page = Math.floor(index / this.getPageSize());

    if (page === this.thumbnailPage) {
      this.updateArrowVisibility();
      return;
    }

    this.thumbnailPage = page;
    this.updateThumbnailPosition();
  }

  getPageSize() {
    const step = this.getThumbnailStep();

    if (!step || !this.viewport) {
      return 4;
    }

    return Math.max(1, Math.round(this.viewport.getBoundingClientRect().width / step));
  }

  getThumbnailPageCount() {
    return Math.max(1, Math.ceil(this.thumbnails.length / this.getPageSize()));
  }

  updateThumbnailPosition() {
    if (!this.track || !this.viewport) {
      return;
    }

    const pageCount = this.getThumbnailPageCount();

    if (this.thumbnailPage >= pageCount) {
      this.thumbnailPage = pageCount - 1;
    }

    if (this.thumbnailPage < 0) {
      this.thumbnailPage = 0;
    }

    const offset = this.thumbnailPage * this.getPageSize() * this.getThumbnailStep();
    this.track.style.transform = `translateX(-${offset}px)`;
    this.updateArrowVisibility();
  }

  updateArrowVisibility() {
    const hasPrev = this.currentIndex > 0;
    const hasNext = this.currentIndex < this.thumbnails.length - 1;

    if (this.prevButton) {
      this.prevButton.classList.toggle('is-hidden', !hasPrev);
      this.prevButton.setAttribute('aria-hidden', String(!hasPrev));
      this.prevButton.tabIndex = hasPrev ? 0 : -1;
    }

    if (this.nextButton) {
      this.nextButton.classList.toggle('is-hidden', !hasNext);
      this.nextButton.setAttribute('aria-hidden', String(!hasNext));
      this.nextButton.tabIndex = hasNext ? 0 : -1;
    }
  }

  getThumbnailStep() {
    if (this.thumbnails.length >= 2) {
      const step = this.thumbnails[1].offsetLeft - this.thumbnails[0].offsetLeft;

      if (step > 0) {
        return step;
      }
    }

    return this.getThumbnailWidth();
  }

  getThumbnailWidth() {
    const firstThumbnail = this.thumbnails[0];

    if (!firstThumbnail) {
      return 0;
    }

    return firstThumbnail.getBoundingClientRect().width;
  }
}
